import ModalManager from '@/utils/modal';
import { ActionContext } from 'vuex';
import { AppState } from '@/models/TechnicianAppState';
import { CurrentJob, JobsContextItem, JobsState, JobsFilters, JobsKeys, Timeslot, AvailableJobsResponseData } from '@/models/JobsState';
import axios, { AxiosResponse, AxiosError } from 'axios';
import config from '@/config';
import { STATE_MODULES } from '@/store/state-modules';
import { ConfigMutations } from '../config/mutations';
import { NOTIFICATION_TYPES, generateNotificationItem } from '@/models/AppConfig';
import { JobsMutations } from './mutations';
import { generateContact, generateDeviceAttributesMap, generateDevices, generateJobDetails } from './helpers';
import ClaimSuccess from '@/components/modals/ClaimSuccess.vue';
import { PaginationData } from '../../../models/Common';
import technicianUtils from '@/utils/technician';
import { LoginActions } from '../login/actions';

export const JobsActions = {
	LOAD_AVAILABLE_JOBS: "LOAD_AVAILABLE_JOBS",
	CHANGE_JOB_CONTEXT: "CHANGE_JOB_CONTEXT",
	LOAD_JOB_DETAILS: "LOAD_JOB_DETAILS",
	MY_JOBS_FILTER_ITEM_CLICKED: 'MY_JOBS_FILTER_ITEM_CLICKED',
	CLAIM_JOB: "CLAIM_JOB",
	START_JOB: "START_JOB"
}

export default {
	[JobsActions.LOAD_AVAILABLE_JOBS] (context: ActionContext<JobsState, AppState>, payload: PaginationData) {
		context.commit(`${STATE_MODULES.JOBS}/${JobsMutations.CLEAR_JOBS}`, {}, { root: true });
		const filtersContext: JobsContextItem = context.state.context;
		axios.post(`${config.api}/portal/technician/jobs`, {
			context: filtersContext.key,
			filters: filtersContext.key === JobsKeys.ASSIGNED ? Array.from(context.state.filters) : [],
			limit: payload?.limit || 20,
			offset: payload?.offset || 0
		}).then((result: AxiosResponse<AvailableJobsResponseData>) => {
			context.commit(`${STATE_MODULES.JOBS}/${JobsMutations.UPDATE_AVAILABLE_JOBS}`, result.data, { root: true });
		}).catch((error: AxiosError) => {
			context.commit(
				`${STATE_MODULES.CONFIG}/${ConfigMutations.ADD_NOTIFICATION}`,
				generateNotificationItem({ type: NOTIFICATION_TYPES.error, message: error.message || "Something wnet wrong!" }),
				{ root: true }
			);
		})
	},
	[JobsActions.CHANGE_JOB_CONTEXT] (context: ActionContext<JobsState, AppState>, payload: JobsContextItem) {
		context.commit(JobsMutations.SWITCH_JOBS_CONTEXT, payload);
		context.dispatch(JobsActions.LOAD_AVAILABLE_JOBS);
	},
	[JobsActions.LOAD_JOB_DETAILS] (context: ActionContext<JobsState, AppState>, uuid: string) {
		context.commit(JobsMutations.CLEAR_CURRENT_JOB)
		axios.get(`${config.api}/portal/appointment/job-offer/details/${uuid}`).then((result: AxiosResponse<any>) => {
			const responseData = result.data.data;
			if (!responseData) {
				throw new Error("cant get appointment details");
			}

			const { contact, details, devices, devices_attributes, customer_images, technician_images, quote_list, checklist } = responseData;
			const deviceAttributesMap = generateDeviceAttributesMap(devices_attributes)

			const job: CurrentJob = {
				uuid,
				contact: generateContact(contact),
				devices: generateDevices(devices, deviceAttributesMap),
				details: generateJobDetails(details),
				customerImages: customer_images,
				technicianImages: technician_images,
				quoteList: quote_list,
				checklist: checklist
			}

			context.commit(JobsMutations.UPDATE_JOB_DETAILS, job);
		}).catch((error: AxiosError<any>) => {
			const { error_code } = error?.response?.data || {};
			context.commit(
				`${STATE_MODULES.CONFIG}/${ConfigMutations.ADD_NOTIFICATION}`,
				generateNotificationItem({ type: NOTIFICATION_TYPES.error, message: error.message || "Something wnet wrong while loading job details!", code: technicianUtils.getErrorFromCode(error_code) }),
				{ root: true }
			);
		})
	},
	[JobsActions.MY_JOBS_FILTER_ITEM_CLICKED] (context: ActionContext<JobsState, AppState>, payload: JobsFilters | number) {
		context.commit(JobsMutations.TOGGLE_FILTER, payload);
		context.dispatch(JobsActions.LOAD_AVAILABLE_JOBS);
	},
	[JobsActions.CLAIM_JOB] (context: ActionContext<JobsState, AppState>, timeslot: Timeslot | null) {
		const uuid = context.state.current_job?.uuid;
		if (!uuid) {
			// eslint-disable-next-line
			console.error("no uuid provided")
			return;
		}
		const params: any = timeslot || {}
		axios.post(`${config.api}/portal/appointment/job-offer/claim/${uuid}`, params).then((result: AxiosResponse<any>) => {
			const { data } = result;
			if (!data.ok) {
				context.commit(
					`${STATE_MODULES.CONFIG}/${ConfigMutations.ADD_NOTIFICATION}`,
					generateNotificationItem({ type: NOTIFICATION_TYPES.error, message: data.message || "Something wnet wrong while claiming the job!" }),
					{ root: true }
				);
			} else {
				ModalManager.open(ClaimSuccess, {}, () => {
					context.dispatch(JobsActions.LOAD_JOB_DETAILS, context.state.current_job?.uuid);
				})
				context.dispatch(`${STATE_MODULES.LOGIN}/${LoginActions.INIT_USER}`, {}, { root: true });	
			}
		}).catch((error: AxiosError) => {
			context.commit(
				`${STATE_MODULES.CONFIG}/${ConfigMutations.ADD_NOTIFICATION}`,
				generateNotificationItem({ type: NOTIFICATION_TYPES.error, message: error.message || "Something wnet wrong while claiming the job!" }),
				{ root: true }
			);
		})
	},
	[JobsActions.START_JOB] (context: ActionContext<JobsState, AppState>) {
		const uuid = context.state.current_job?.uuid;
		if (!uuid) {
			return context.commit(
				`${STATE_MODULES.CONFIG}/${ConfigMutations.ADD_NOTIFICATION}`,
				generateNotificationItem({ type: NOTIFICATION_TYPES.error, message: "Something wnet wrong | No UUID" }),
				{ root: true }
			);
		}

		axios.post(`${config.api}/portal/appointment/start-job`, { uuid }).then((result: AxiosResponse<any>) => {
			if (result.data.ok) {
				context.dispatch(JobsActions.LOAD_JOB_DETAILS, uuid);
			}
		}).catch((error: AxiosError) => {
			context.commit(
				`${STATE_MODULES.CONFIG}/${ConfigMutations.ADD_NOTIFICATION}`,
				generateNotificationItem({ type: NOTIFICATION_TYPES.error, message: error.message || "Something wnet wrong while starting the job!" }),
				{ root: true }
			);
		})
	}
}
